import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';

function Tooltip({ label }: { label: string }) {
  return (
    <div className="absolute left-16 top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded-xl bg-slate-950 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-200 pointer-events-none group-hover:opacity-100">
      {label}
    </div>
  );
}

export function Navigation() {
  const location = useLocation();
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(max-width: 768px)').matches;
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const stored = window.localStorage.getItem('sidebar-collapsed');
    if (stored !== null) {
      return stored === 'true';
    }

    return window.matchMedia('(max-width: 1024px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const root = document.documentElement;

    if (isMobile) {
      root.style.setProperty('--sidebar-width', '0px');
      return;
    }

    root.style.setProperty(
      '--sidebar-width',
      isCollapsed ? 'var(--sidebar-width-collapsed)' : 'var(--sidebar-width-expanded)'
    );
    window.localStorage.setItem('sidebar-collapsed', String(isCollapsed));
  }, [isCollapsed, isMobile]);

  const menuItems = [
    {
      path: '/dashboard',
      label: 'Visão geral',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 13.5 12 6l8 7.5M6.5 12v7h11v-7M10 19v-5h4v5" />
        </svg>
      )
    },
    {
      path: '/contatos',
      label: 'Eleitores',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7Z" />
        </svg>
      )
    },
    {
      path: '/campanhas',
      label: 'Campanhas',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5.882V19.24a1.76 1.76 0 0 1-3.417.592l-2.147-6.15M18 13a3 3 0 1 0 0-6M5.436 13.683A4.001 4.001 0 0 1 7 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 0 1-1.564-.317Z" />
        </svg>
      )
    },
    {
      path: '/territorios',
      label: 'Territórios',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 21s7-4.438 7-11a7 7 0 1 0-14 0c0 6.562 7 11 7 11Z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
        </svg>
      )
    },
    {
      path: '/analytics',
      label: 'Analytics',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 19V5m0 14h16M8 17v-6m4 6V7m4 10v-9" />
        </svg>
      )
    },
    ...(['ADMIN', 'TENANT_ADMIN', 'SUPERADMIN'].includes(user?.role || '') ? [{
      path: '/usuarios',
      label: 'Usuários',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm0 11c-4.418 0-8 2.239-8 5h16c0-2.761-3.582-5-8-5Zm7-7a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z" />
        </svg>
      )
    }] : []),
    ...(user?.role === 'SUPERADMIN' ? [{
      path: '/super-admin',
      label: 'Super Admin',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 3 4 7v6c0 4.418 3.582 7.582 8 8 4.418-.418 8-3.582 8-8V7l-8-4Zm-2.5 9.5 1.75 1.75L14.5 11" />
        </svg>
      )
    }] : []),
    {
      path: '/configuracoes',
      label: 'Configurações',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065Z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      )
    }
  ];

  if (isMobile) {
    return (
      <nav className="sidebar-navigation border-t border-slate-200/70 bg-white/95 px-3 shadow-[0_-12px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl">
        <div className="flex h-full items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-primary text-white shadow-glow">
            {settings?.iconUrl ? (
              <img
                src={settings.iconUrl}
                alt={settings?.companyName || 'Sistema'}
                className="max-h-6 max-w-6 object-contain"
              />
            ) : (
              <span className="text-sm font-bold">
                {settings?.companyName?.charAt(0)?.toUpperCase() || 'V'}
              </span>
            )}
          </div>

          <ul className="no-scrollbar flex flex-1 items-center gap-2 overflow-x-auto py-2 pr-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <li key={item.path} className="shrink-0">
                  <Link
                    to={item.path}
                    className={`flex min-w-[122px] items-center gap-2 rounded-2xl px-3 py-2.5 text-xs font-semibold transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-primary text-white shadow-soft'
                        : 'bg-muted text-muted-foreground hover:bg-accent/10 hover:text-foreground'
                    }`}
                    title={item.label}
                  >
                    {item.icon}
                    <span className="truncate">{item.label}</span>
                  </Link>
                </li>
              );
            })}
            <li className="shrink-0">
              <button
                onClick={logout}
                className="flex min-w-[96px] items-center gap-2 rounded-2xl bg-rose-50 px-3 py-2.5 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-100"
                title="Sair"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 16l4-4m0 0-4-4m4 4H9m4 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1" />
                </svg>
                <span>Sair</span>
              </button>
            </li>
          </ul>
        </div>
      </nav>
    );
  }

  return (
    <nav className="sidebar-navigation flex flex-col border-r border-white/10 bg-gradient-cta text-white shadow-[20px_0_60px_rgba(32,42,82,0.16)] transition-all duration-200">
      <div className="flex-1 p-4">
        <div className={`mb-8 flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-2'}`}>
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10 backdrop-blur-sm">
            {settings?.iconUrl ? (
              <img
                src={settings.iconUrl}
                alt={settings?.companyName || 'Vox Campaign'}
                className="max-h-8 max-w-8 object-contain"
              />
            ) : (
              <span className="text-lg font-bold text-white">
                {settings?.companyName?.charAt(0)?.toUpperCase() || 'V'}
              </span>
            )}
          </div>

          {!isCollapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{settings?.companyName || 'Vox Campaign'}</p>
              <p className="text-xs text-white/60">Workspace ativo</p>
            </div>
          )}
        </div>

        <div className="mb-4 px-2">
          {!isCollapsed && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/40">Navegação</p>
          )}
        </div>

        <ul className="space-y-2.5">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={`group relative flex min-w-0 items-center rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/30 ${
                    isCollapsed ? 'mx-auto h-12 w-12 justify-center' : 'w-full gap-3 px-3 py-3'
                  } ${
                    isActive
                      ? 'border border-white/10 bg-white text-slate-900 shadow-[0_14px_30px_rgba(15,23,42,0.16)]'
                      : 'text-white/72 hover:bg-white/10 hover:text-white'
                  }`}
                  title={item.label}
                >
                  {item.icon}
                  {!isCollapsed && <span className="truncate text-sm font-medium">{item.label}</span>}
                  {isCollapsed && <Tooltip label={item.label} />}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="space-y-2 border-t border-white/10 p-4">
        <button
          onClick={() => setIsCollapsed((prev) => !prev)}
          className={`group relative flex items-center rounded-2xl text-white/72 transition-all duration-200 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 ${
            isCollapsed ? 'mx-auto h-12 w-12 justify-center' : 'w-full gap-3 px-3 py-3'
          }`}
          title={isCollapsed ? 'Expandir menu' : 'Minimizar menu'}
          aria-label={isCollapsed ? 'Expandir menu' : 'Minimizar menu'}
        >
          <svg className={`h-5 w-5 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 19l-7-7 7-7" />
          </svg>
          {!isCollapsed && <span className="text-sm font-medium">Minimizar</span>}
          {isCollapsed && <Tooltip label="Expandir" />}
        </button>

        <button
          onClick={logout}
          className={`group relative flex items-center rounded-2xl text-white/72 transition-all duration-200 hover:bg-rose-500/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 ${
            isCollapsed ? 'mx-auto h-12 w-12 justify-center' : 'w-full gap-3 px-3 py-3'
          }`}
          title="Sair"
          aria-label="Sair"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 16l4-4m0 0-4-4m4 4H9m4 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1" />
          </svg>
          {!isCollapsed && <span className="text-sm font-medium">Sair</span>}
          {isCollapsed && <Tooltip label="Sair" />}
        </button>
      </div>
    </nav>
  );
}
